import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';

/**
 * Badge — katalog Design System (`acm-badge`).
 *
 * Primární varianty (katalog):
 *   success | warning | error | info | default
 *
 * Aliasy (zpětná kompatibilita):
 *   green→success, yellow→warning, red→error, blue→info,
 *   gray→default, purple, secondary→default
 */
export type BadgeVariant =
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'default'
  // aliases
  | 'green'
  | 'yellow'
  | 'red'
  | 'blue'
  | 'gray'
  | 'purple'
  | 'secondary';

const CANONICAL: Record<BadgeVariant, 'success' | 'warning' | 'error' | 'info' | 'default' | 'purple'> = {
  success: 'success',
  warning: 'warning',
  error: 'error',
  info: 'info',
  default: 'default',
  green: 'success',
  yellow: 'warning',
  red: 'error',
  blue: 'info',
  gray: 'default',
  purple: 'purple',
  secondary: 'default',
};

const VARIANTS: Record<'success' | 'warning' | 'error' | 'info' | 'default' | 'purple', string> = {
  success: 'border-emerald-500/40 bg-emerald-500/18 text-emerald-200 shadow-sm',
  warning: 'border-amber-500/40 bg-amber-500/18 text-amber-200 shadow-sm',
  error: 'border-red-500/40 bg-red-500/18 text-red-200 shadow-sm',
  info: 'border-plex/40 bg-plex/18 text-plex shadow-sm',
  default: 'border-gray-600/50 bg-gray-800/85 text-gray-200 shadow-sm',
  purple: 'border-violet-500/40 bg-violet-500/18 text-violet-200 shadow-sm',
};

const DOT: Record<'success' | 'warning' | 'error' | 'info' | 'default' | 'purple', string> = {
  success: 'bg-emerald-400',
  warning: 'bg-amber-400',
  error: 'bg-red-400',
  info: 'bg-plex',
  default: 'bg-gray-400',
  purple: 'bg-violet-400',
};

interface BadgeProps {
  variant?: BadgeVariant;
  dot?: boolean;
  children: ReactNode;
  className?: string;
}

/**
 * Konzistentní badge/tag pro stavy, typy a štítky.
 *
 * Použití:
 *   <Badge variant="success">active</Badge>
 *   <Badge variant="error" dot>offline</Badge>
 *   <Badge variant="info">core</Badge>
 */
export function Badge({ variant = 'default', dot = false, children, className }: BadgeProps) {
  const key = CANONICAL[variant] ?? 'default';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-medium leading-none tracking-wide',
        VARIANTS[key],
        className,
      )}
    >
      {dot && (
        <span className={cn('h-1.5 w-1.5 flex-shrink-0 rounded-full', DOT[key])} />
      )}
      {children}
    </span>
  );
}
